import React, { useState, useRef, useEffect } from "react";
import { Modal, TextInput, Button, Group } from "@mantine/core";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import {
	IconCode,
	IconCodeblock,
	IconList,
	IconListCheck,
	IconListNumbers,
	IconLock,
} from "@tabler/icons-react";

export default function RichTextEditor({
	content,
	onChange,
	onInit,
	disabled,
	// unlike disabled, can change after mount and keeps a text cursor so it is
	// clear the text can still be selected and copied. Shows readOnlyMessage
	// in place of the toolbar
	readOnly,
	readOnlyMessage,
	// rendered to the right of the content, below the toolbar
	sidePanel,
}) {
	const editable = !disabled && !readOnly;
	const [linkDialogOpen, setLinkDialogOpen] = useState(false);
	const [linkUrl, setLinkUrl] = useState("");
	const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);
	const [hoveredLink, setHoveredLink] = useState(null);
	const pendingEditorRef = useRef(null);

	const openLinkDialog = (editor) => {
		const existingHref = editor.getAttributes("link").href || "";
		pendingEditorRef.current = editor;
		setLinkUrl(existingHref);
		setIsEditingExistingLink(!!existingHref);
		setLinkDialogOpen(true);
	};

	const applyLink = () => {
		if (pendingEditorRef.current) {
			if (linkUrl) {
				pendingEditorRef.current
					.chain()
					.focus()
					.setLink({ href: linkUrl })
					.run();
			} else {
				pendingEditorRef.current.chain().focus().unsetLink().run();
			}
		}
		setLinkDialogOpen(false);
	};

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				link: {
					openOnClick: false,
					HTMLAttributes: {
						target: "_blank",
						rel: "noopener noreferrer",
					},
				},
				underline: {},
			}),
			TaskList,
			TaskItem.configure({ nested: true }),
			Extension.create({
				addKeyboardShortcuts() {
					return {
						"Mod-k": () => {
							openLinkDialog(this.editor);
							return true;
						},
					};
				},
			}),
		],
		content: content || "",
		editable,
		editorProps: {
			attributes: {
				class: "NoteHtmlContent",
			},
		},
		onCreate: ({ editor }) => {
			onInit?.(editor.getHTML());
		},
		onUpdate: ({ editor }) => {
			onChange(editor.getHTML());
		},
	});

	useEffect(() => {
		if (editor && editor.isEditable !== editable) {
			editor.setEditable(editable);
		}
	}, [editor, editable]);

	return (
		<>
			<Modal
				opened={linkDialogOpen}
				onClose={() => setLinkDialogOpen(false)}
				title="Edit Link"
				size="sm"
			>
				<TextInput
					label="URL"
					placeholder="https://..."
					value={linkUrl}
					onChange={(e) => setLinkUrl(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && applyLink()}
					data-autofocus
				/>
				<Group justify="flex-end" mt="md">
					<Button
						variant="default"
						onClick={() => setLinkDialogOpen(false)}
					>
						Cancel
					</Button>
					<Button
						onClick={applyLink}
						disabled={!isEditingExistingLink && !linkUrl}
					>
						{isEditingExistingLink && !linkUrl ? "Remove" : "Save"}
					</Button>
				</Group>
			</Modal>
			<div
				className="NoteTextArea"
				style={{
					height: "100%",
					boxSizing: "border-box",
					minHeight: 400,
					display: "flex",
					flexDirection: "column",
				}}
				onMouseOver={(e) => {
					const link = e.target.closest("a[href]");
					if (link) {
						const rect = link.getBoundingClientRect();
						setHoveredLink({
							href: link.getAttribute("href"),
							top: rect.bottom + 4,
							left: rect.left,
						});
					} else {
						setHoveredLink(null);
					}
				}}
				onMouseLeave={() => setHoveredLink(null)}
			>
				{!disabled && readOnly && (
					<div
						className="RichTextEditorToolbar"
						role="note"
						style={{
							alignItems: "center",
							gap: 8,
							padding: "6px 12px",
							fontSize: 14,
							fontWeight: 500,
							color: "var(--mantine-color-orange-light-color)",
							background: "var(--mantine-color-orange-light)",
						}}
					>
						<IconLock size={16} />
						{readOnlyMessage || "Read only"}
					</div>
				)}
				{!disabled && !readOnly && editor && (
					<div className="RichTextEditorToolbar">
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().undo().run()
							}
							disabled={!editor.can().undo()}
							title="Undo"
						>
							↩
						</ToolbarButton>
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().redo().run()
							}
							disabled={!editor.can().redo()}
							title="Redo"
						>
							↪
						</ToolbarButton>
						<Divider />
						{[1, 2, 3, 4].map((level) => (
							<ToolbarButton
								key={level}
								onMouseDown={() =>
									editor
										.chain()
										.focus()
										.toggleHeading({ level })
										.run()
								}
								active={editor.isActive("heading", { level })}
							>
								H{level}
							</ToolbarButton>
						))}
						<Divider />
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().toggleBold().run()
							}
							active={editor.isActive("bold")}
							style={{ fontWeight: "bold" }}
						>
							B
						</ToolbarButton>
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().toggleItalic().run()
							}
							active={editor.isActive("italic")}
							style={{ fontStyle: "italic" }}
						>
							I
						</ToolbarButton>
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().toggleUnderline().run()
							}
							active={editor.isActive("underline")}
							style={{ textDecoration: "underline" }}
						>
							U
						</ToolbarButton>
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().toggleStrike().run()
							}
							active={editor.isActive("strike")}
							style={{ textDecoration: "line-through" }}
						>
							S
						</ToolbarButton>
						<Divider />
						<ToolbarButton
							onMouseDown={() => openLinkDialog(editor)}
							active={editor.isActive("link")}
						>
							Link
						</ToolbarButton>
						<Divider />
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().toggleBulletList().run()
							}
							active={editor.isActive("bulletList")}
						>
							<IconList
								size={16}
								style={{ verticalAlign: "sub" }}
							/>
						</ToolbarButton>
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().toggleOrderedList().run()
							}
							active={editor.isActive("orderedList")}
						>
							<IconListNumbers
								size={16}
								style={{ verticalAlign: "sub" }}
							/>
						</ToolbarButton>
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().toggleTaskList().run()
							}
							active={editor.isActive("taskList")}
							title="Checklist"
						>
							<IconListCheck
								size={16}
								style={{ verticalAlign: "sub" }}
							/>
						</ToolbarButton>
						<Divider />
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().toggleCode().run()
							}
							active={editor.isActive("code")}
							style={{ fontFamily: "monospace" }}
						>
							<IconCode
								size={16}
								style={{ verticalAlign: "sub" }}
							/>
						</ToolbarButton>
						<ToolbarButton
							onMouseDown={() =>
								editor.chain().focus().toggleCodeBlock().run()
							}
							active={editor.isActive("codeBlock")}
							style={{ fontFamily: "monospace" }}
						>
							<IconCodeblock
								size={16}
								style={{ verticalAlign: "sub" }}
							/>
						</ToolbarButton>
					</div>
				)}
				<div style={{ display: "flex", flexGrow: 1, minHeight: 0 }}>
					<div
						className="NoteTextAreaContent"
						style={{
							flexGrow: 1,
							minWidth: 0,
							overflowY: "auto",
							display: "flex",
							flexDirection: "column",
							backgroundColor: disabled
								? "hsl(0deg 0% 93.33%)"
								: readOnly
									? "var(--mantine-color-gray-0)"
									: undefined,
							cursor: disabled ? "not-allowed" : undefined,
						}}
						onClick={() =>
							editor && editable && editor.commands.focus()
						}
					>
						<EditorContent
							editor={editor}
							style={{ flexGrow: 1 }}
						/>
					</div>
					{sidePanel}
				</div>
			</div>
			{hoveredLink && (
				<div
					style={{
						position: "fixed",
						top: hoveredLink.top,
						left: hoveredLink.left,
						background: "#333",
						color: "white",
						padding: "3px 8px",
						borderRadius: 4,
						fontSize: 12,
						zIndex: 1000,
						maxWidth: 320,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						pointerEvents: "none",
					}}
				>
					{hoveredLink.href}
				</div>
			)}
		</>
	);
}

function ToolbarButton({
	onMouseDown,
	active,
	disabled,
	style,
	title,
	children,
}) {
	return (
		<button
			onMouseDown={(e) => {
				e.preventDefault();
				if (!disabled) onMouseDown();
			}}
			title={title}
			style={{
				background: active ? "#e8e8e8" : "transparent",
				border: "none",
				borderRadius: 3,
				padding: "2px 8px",
				cursor: disabled ? "default" : "pointer",
				fontSize: 14,
				opacity: disabled ? 0.4 : 1,
				...style,
			}}
		>
			{children}
		</button>
	);
}

function Divider() {
	return (
		<span
			style={{
				width: 1,
				background: "#d0d0d0",
				margin: "2px 4px",
				alignSelf: "stretch",
			}}
		/>
	);
}
